import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { AxiosError } from 'axios'
import { Collapse, Modal, notification, Tooltip } from 'antd'
import { SaveFilled } from '@ant-design/icons'
import styled from 'styled-components'
import _ from 'lodash'
import { colours, sizes } from '@qflow/theme'

import { ColourButton, icons, Text } from 'portal/lib/primitives'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

import { ProjectsData, TitleBar } from './SettingsStyledComponents'

import { AutodeskConfiguration } from './Integrations/AutodeskConfiguration'
import {
  autodeskConfigurationDelete,
  autodeskConfigurationPut
} from './Integrations/AutoDeskConfigurationAPI'

export const Integrations = ({
  project,
  projectsReloader
}: IntegrationsProps) => {
  const [autodeskConfiguration, setAutodeskConfiguration] =
    useState<AutodeskConfigurationType | null>()
  const [autodeskResetKey, setAutodeskResetKey] = useState(0)

  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setAutodeskConfiguration(project?.autodeskConfiguration)
  }, [project?.autodeskConfiguration])

  const dispatch = useDispatch()
  const { accountId } = useParams<{ accountId: string }>()
  const qflowProjectId = project?.projectId

  const { data: autodeskData } = useSelector((state: any) => {
    return state.integrationAutodesk
  })
  const { data: autodeskHubData } = useSelector((state: any) => {
    return state.integrationAutodeskHub
  })

  const showSuccessNotification = () => {
    notification.success({
      message: `Integration successfully updated`,
      duration: 5,
      placement: 'top'
    })
  }

  const showFailureNotification = (errorData?: { errors?: any }) => {
    console.error('Integration update failed:', errorData)

    let errorMessage = 'Please check the entered values.'
    // Create a user-friendly error message based on the errorData.
    // If there are specific error messages from the backend, display those.
    // Otherwise, show a generic error message.
    // Expecting errorData.errors to be an object where keys are field names and values are arrays of error messages for that field.
    if (errorData?.errors) {
      errorMessage = Object.entries(errorData.errors)
        .map(([field, messages]) => {
          if (Array.isArray(messages)) {
            return `${messages.join(', ')}`
          }
          return `${String(messages)}`
        })
        .join('\n')
    }

    // If autodeskData has a regions key and it's an array, replace single quoted value keys in the error message with the corresponding region names.
    if (autodeskData?.regions && Array.isArray(autodeskData.regions)) {
      autodeskData.regions.forEach(region => {
        errorMessage = errorMessage.replace(
          new RegExp(`'${region.value}'`, 'g'),
          `'${region.name}'`
        )
      })
    }

    // If autodeskHubData has a projects key and it's an array, replace single quoted id keys in the error message with the corresponding project names.
    if (autodeskHubData?.projects && Array.isArray(autodeskHubData.projects)) {
      autodeskHubData.projects.forEach(project => {
        errorMessage = errorMessage.replace(
          new RegExp(`'${project.id}'`, 'g'),
          `'${project.name}'`
        )
      })
    }

    notification.error({
      message: `Integration failed to be updated`,
      description: errorMessage,
      duration: 10,
      placement: 'top'
    })
  }

  const handleAutodeskConfigurationSave = () => {
    Modal.confirm({
      title: 'Save configuration',
      content: (
        <>
          <p>Are you sure you want to save this configuration?</p>
          <p>Click OK to continue.</p>
        </>
      ),
      onOk: () => {
        dispatch(
          trackMixpanel('frontend_intergration_configuration_save_click', {
            account_id: accountId,
            project_id: qflowProjectId,
            configuration: 'autodesk'
          })
        )

        setSaving(true)

        if (
          accountId &&
          autodeskConfiguration &&
          autodeskConfiguration.hubId &&
          autodeskConfiguration.projectId &&
          autodeskConfiguration.region
        ) {
          autodeskConfigurationPut(
            accountId,
            qflowProjectId,
            autodeskConfiguration.projectId,
            autodeskConfiguration.hubId,
            autodeskConfiguration.region
          )
            .then(() => {
              showSuccessNotification()
            })
            .catch(err => {
              const error = err as AxiosError<{ errors?: any }>
              showFailureNotification(error?.response?.data)
            })
            .finally(() => {
              dispatch(projectsReloader(accountId))
              setSaving(false)
            })
        }
      }
    })
  }

  const handleAutodeskConfigurationDelete = () => {
    Modal.confirm({
      title: 'Delete Autodesk configuration',
      content: 'This action cannot be undone. Are you sure?',
      autoFocusButton: null,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => {
        dispatch(
          trackMixpanel('frontend_intergration_configuration_delete_click', {
            account_id: accountId,
            project_id: qflowProjectId,
            configuration: 'autodesk'
          })
        )

        if (accountId) {
          setSaving(true)
          autodeskConfigurationDelete(accountId, qflowProjectId)
            .then(() => {
              setAutodeskConfiguration(null)
              showSuccessNotification()
            })
            .catch(err => {
              const error = err as AxiosError<{ errors?: any }>
              showFailureNotification(error?.response?.data)
            })
            .finally(() => {
              dispatch(projectsReloader(accountId))
              setSaving(false)
            })
        }
      }
    })
  }

  const resetDisabled = useMemo(() => {
    // The Reset button should be disabled if the current autodeskConfiguration is the same as the original one (as it means there are no changes to reset)
    return _.isEqual(autodeskConfiguration, project?.autodeskConfiguration)
  }, [autodeskConfiguration, project?.autodeskConfiguration])

  const saveDisabled = useMemo(() => {
    // If autodeskConfiguration is null/undefined, then the save button should be enabled if there is any data in the form (as it means we are creating a new configuration)
    if (!project?.autodeskConfiguration) {
      return !autodeskConfiguration
    }
    // If autodeskConfiguration does not have a hubId, projectId or region, then the save button should be disabled (as these are required fields)
    if (
      !autodeskConfiguration?.hubId ||
      !autodeskConfiguration?.projectId ||
      !autodeskConfiguration?.region
    ) {
      return true
    }
    // Otherwise, the save button should be disabled if the current autodeskConfiguration is the same as the original one (as it means there are no changes to save)
    return _.isEqual(autodeskConfiguration, project?.autodeskConfiguration)
  }, [autodeskConfiguration, project?.autodeskConfiguration])

  return (
    <ProjectsData id="alert-project-data">
      <TitleBar>
        <h3>{project?.name}</h3>
      </TitleBar>
      <Collapse
        defaultActiveKey={['autodesk']}
        items={[
          {
            key: 'autodesk',
            label: (
              <IntegrationTitleBar>
                <IntegrationTitle>Autodesk</IntegrationTitle>
                {project?.isAutodeskEnabled && (
                  <IntegrationButtons className="hide-if-closed">
                    <Tooltip
                      title={
                        resetDisabled
                          ? 'No changes to reset'
                          : 'Reset to saved values'
                      }
                    >
                      <ColourButton.BasicBorderless
                        id={`ResetButton-autodesk`}
                        aria-label="Reset alert"
                        style={{ padding: '4px 9px', borderRadius: '50%' }}
                        disabled={!autodeskConfiguration || resetDisabled}
                        onClick={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          setAutodeskConfiguration(
                            project.autodeskConfiguration
                          )
                          setAutodeskResetKey(k => k + 1)
                        }}
                      >
                        <icons.Restore
                          colour="currentColor"
                          size={sizes.X_SMALL}
                        />
                      </ColourButton.BasicBorderless>
                    </Tooltip>

                    <Tooltip title="Remove configuration">
                      <ColourButton.BasicBorderless
                        id={`RemoveButton-autodesk`}
                        aria-label="Remove configuration"
                        style={{ padding: '4px 9px', borderRadius: '50%' }}
                        onClick={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleAutodeskConfigurationDelete()
                        }}
                        disabled={!project?.autodeskConfiguration}
                      >
                        <icons.TrashCan
                          colour={
                            project?.autodeskConfiguration
                              ? colours.ERROR
                              : colours.CLOUD_GREY
                          }
                        />
                      </ColourButton.BasicBorderless>
                    </Tooltip>

                    <Tooltip
                      title={
                        saveDisabled
                          ? 'No changes to save'
                          : 'Save configuration'
                      }
                    >
                      <ColourButton.BasicBorderless
                        id={`SaveButton-autodesk`}
                        aria-label="Save configuration"
                        style={{ padding: '5px 7px 3px', borderRadius: '50%' }}
                        onClick={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleAutodeskConfigurationSave()
                        }}
                        disabled={saveDisabled}
                      >
                        <SaveFilled
                          style={{
                            fontSize: sizes.MEDIUM,
                            color: saveDisabled
                              ? colours.CLOUD_GREY
                              : colours.TEAL_HOVER
                          }}
                        />
                      </ColourButton.BasicBorderless>
                    </Tooltip>
                  </IntegrationButtons>
                )}
              </IntegrationTitleBar>
            ),
            children: (
              <>
                {!project?.isAutodeskEnabled && (
                  <Section
                    style={{
                      backgroundColor: colours.WHITE,
                      marginTop: '1rem'
                    }}
                  >
                    <Text size={sizes.MEDIUM}>
                      <p>
                        Autodesk integration is not enabled for this project.
                      </p>
                      <p>
                        Please speak with your Customer Success Manager to find
                        out more information, or contact us at{' '}
                        <a href="mailto:support@qualisflow.com">
                          support@qualisflow.com
                        </a>
                      </p>
                    </Text>
                  </Section>
                )}
                {project?.isAutodeskEnabled && (
                  <AutodeskConfiguration
                    autodeskConfiguration={autodeskConfiguration}
                    setAutodeskConfiguration={setAutodeskConfiguration}
                    startingHubId={project?.autodeskConfiguration?.hubId}
                    saving={saving}
                    resetKey={autodeskResetKey}
                    setResetKey={setAutodeskResetKey}
                  />
                )}
              </>
            )
          }
        ]}
      />
    </ProjectsData>
  )
}

type AutodeskConfigurationType = {
  hubId: string | null | undefined
  projectId: string | null | undefined
  region: string | null | undefined
}

export interface ProjectIntergrationsSettings {
  name: string
  projectId: string
  isAutodeskEnabled: boolean
  autodeskConfiguration: AutodeskConfigurationType
}

type IntegrationsProps = {
  project: ProjectIntergrationsSettings
  projectsReloader: (accountId: string) => any
}

const Section = styled.div`
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 0.625rem;
  padding: 1rem 1.5rem;
  margin-bottom: 0.625rem;

  & > h3 {
    color: ${colours.CORE_NAVY};
    font-family: 'Jakarta Sans', 'Inter', sans-serif !important;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  & > h4 {
    color: ${colours.CORE_NAVY};
    font-family: 'Jakarta Sans', 'Inter', sans-serif !important;
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  & > p:last-child {
    margin-bottom: 0;
  }
`
const IntegrationTitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 2rem;
`
const IntegrationTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${colours.CORE_NAVY};
`
const IntegrationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`
